import React, { useEffect, useState, useCallback } from 'react';
import { 
  getChartOfAccounts, 
  createAccount, 
  seedDefaultAccounts,
  getAccountingPeriods,
  createAccountingPeriod,
  closeAccountingPeriod,
  getJournalEntries,
  createJournalEntry,
  approveJournalEntry,
  postJournalEntry,
  getDayBook,
  getGeneralLedger,
  getTrialBalanceReport,
  getAuditLog,
  getCompanies
} from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BookOpen, 
  FileText, 
  Calendar, 
  CheckCircle2, 
  AlertCircle, 
  Plus, 
  Eye,
  Lock,
  Unlock,
  ArrowUpDown,
  TrendingUp,
  TrendingDown,
  DollarSign,
  History,
  Settings,
  Download,
  RefreshCw,
  Search,
  ChevronRight,
  ChevronDown,
  Building2,
  Scale
} from 'lucide-react';
import { toast } from 'sonner';

// Account type colors
const accountTypeColors = {
  asset: 'bg-blue-100 text-blue-800',
  liability: 'bg-red-100 text-red-800',
  equity: 'bg-purple-100 text-purple-800',
  revenue: 'bg-green-100 text-green-800',
  expense: 'bg-orange-100 text-orange-800',
  contra_asset: 'bg-blue-50 text-blue-600',
  contra_liability: 'bg-red-50 text-red-600',
};

const periodStatusColors = {
  open: 'bg-green-100 text-green-800',
  soft_close: 'bg-amber-100 text-amber-800',
  hard_close: 'bg-red-100 text-red-800',
};

const journalStatusColors = {
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  posted: 'bg-green-100 text-green-800',
  reversed: 'bg-red-100 text-red-800',
};

export const AccountingCorePage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [activeTab, setActiveTab] = useState('chart-of-accounts');
  const [loading, setLoading] = useState(false);

  // Chart of Accounts state
  const [accounts, setAccounts] = useState([]);
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [newAccount, setNewAccount] = useState({});

  // Periods state
  const [periods, setPeriods] = useState([]);
  const [periodDialogOpen, setPeriodDialogOpen] = useState(false);

  // Journal Entries state
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);
  const [journalLines, setJournalLines] = useState([
    { account_id: '', account_code: '', account_name: '', debit_amount: 0, credit_amount: 0 },
    { account_id: '', account_code: '', account_name: '', debit_amount: 0, credit_amount: 0 }
  ]);
  const [journalHeader, setJournalHeader] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    description: '',
    reference_number: '',
    entry_type: 'standard'
  });

  // Day Book state
  const [dayBookDate, setDayBookDate] = useState(new Date().toISOString().split('T')[0]);
  const [dayBookData, setDayBookData] = useState(null);

  // General Ledger state
  const [selectedAccountForGL, setSelectedAccountForGL] = useState('');
  const [glData, setGlData] = useState(null);

  // Trial Balance state
  const [trialBalanceDate, setTrialBalanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [trialBalanceData, setTrialBalanceData] = useState(null);

  // Audit Log state
  const [auditLogs, setAuditLogs] = useState([]);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadAccounts();
      loadPeriods();
      loadJournalEntries();
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await getChartOfAccounts(selectedCompany, null, true);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPeriods = async () => {
    try {
      const response = await getAccountingPeriods(selectedCompany);
      setPeriods(response.data);
    } catch (error) {
      console.error('Failed to load periods:', error);
    }
  };

  const loadJournalEntries = async () => {
    try {
      const response = await getJournalEntries(selectedCompany, { limit: 50 });
      setJournalEntries(response.data.entries || []);
    } catch (error) {
      console.error('Failed to load journal entries:', error);
    }
  };

  const handleSeedAccounts = async () => {
    setLoading(true);
    try {
      await seedDefaultAccounts(selectedCompany);
      toast.success('Chart of Accounts seeded successfully!');
      loadAccounts();
      loadPeriods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJournalEntry = async () => {
    // Validate
    const totalDebit = journalLines.reduce((sum, l) => sum + (parseFloat(l.debit_amount) || 0), 0);
    const totalCredit = journalLines.reduce((sum, l) => sum + (parseFloat(l.credit_amount) || 0), 0);
    
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      toast.error(`Entry not balanced. Debits: ${totalDebit.toFixed(2)}, Credits: ${totalCredit.toFixed(2)}`);
      return;
    }

    try {
      await createJournalEntry({
        company_id: selectedCompany,
        ...journalHeader,
        lines: journalLines.filter(l => l.account_id && (l.debit_amount > 0 || l.credit_amount > 0))
      });
      toast.success('Journal entry created!');
      setJournalDialogOpen(false);
      loadJournalEntries();
      // Reset form
      setJournalLines([
        { account_id: '', account_code: '', account_name: '', debit_amount: 0, credit_amount: 0 },
        { account_id: '', account_code: '', account_name: '', debit_amount: 0, credit_amount: 0 }
      ]);
      setJournalHeader({
        entry_date: new Date().toISOString().split('T')[0],
        description: '',
        reference_number: '',
        entry_type: 'standard'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create journal entry');
    }
  };

  const handleApproveEntry = async (entryId) => {
    try {
      await approveJournalEntry(entryId);
      toast.success('Journal entry approved!');
      loadJournalEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handlePostEntry = async (entryId) => {
    try {
      await postJournalEntry(entryId);
      toast.success('Journal entry posted to GL!');
      loadJournalEntries();
      loadAccounts(); // Refresh balances
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    }
  };

  const loadDayBook = async () => {
    try {
      const response = await getDayBook(selectedCompany, dayBookDate);
      setDayBookData(response.data);
    } catch (error) {
      toast.error('Failed to load day book');
    }
  };

  const loadGeneralLedger = async () => {
    if (!selectedAccountForGL) {
      toast.error('Please select an account');
      return;
    }
    try {
      const response = await getGeneralLedger(selectedCompany, selectedAccountForGL);
      setGlData(response.data);
    } catch (error) {
      toast.error('Failed to load general ledger');
    }
  };

  const loadTrialBalance = async () => {
    try {
      const response = await getTrialBalanceReport(selectedCompany, trialBalanceDate);
      setTrialBalanceData(response.data);
    } catch (error) {
      toast.error('Failed to load trial balance');
    }
  };

  const loadAuditLog = async () => {
    try {
      const response = await getAuditLog(selectedCompany, { limit: 100 });
      setAuditLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit log');
    }
  };

  const addJournalLine = () => {
    setJournalLines([...journalLines, { account_id: '', account_code: '', account_name: '', debit_amount: 0, credit_amount: 0 }]);
  };

  const updateJournalLine = (index, field, value) => {
    const updated = [...journalLines];
    updated[index][field] = value;
    
    // If selecting account, populate code and name
    if (field === 'account_id') {
      const account = accounts.find(a => a.id === value);
      if (account) {
        updated[index].account_code = account.account_code;
        updated[index].account_name = account.account_name;
      }
    }
    
    setJournalLines(updated);
  };

  const removeJournalLine = (index) => {
    if (journalLines.length > 2) {
      setJournalLines(journalLines.filter((_, i) => i !== index));
    }
  };

  // Calculate totals for journal entry
  const journalTotalDebit = journalLines.reduce((sum, l) => sum + (parseFloat(l.debit_amount) || 0), 0);
  const journalTotalCredit = journalLines.reduce((sum, l) => sum + (parseFloat(l.credit_amount) || 0), 0);
  const journalIsBalanced = Math.abs(journalTotalDebit - journalTotalCredit) < 0.01;

  // Build hierarchical account tree
  const buildAccountTree = () => {
    const rootAccounts = accounts.filter(a => !a.parent_account_id);
    const getChildren = (parentId) => accounts.filter(a => a.parent_account_id === parentId);
    
    const renderAccount = (account, depth = 0) => {
      const children = getChildren(account.id);
      const hasChildren = children.length > 0;
      const isExpanded = expandedAccounts.has(account.id);
      
      return (
        <div key={account.id}>
          <div 
            className={`flex items-center justify-between py-2 px-3 hover:bg-accent/5 cursor-pointer border-b ${account.is_header ? 'font-semibold bg-slate-50' : ''}`}
            style={{ paddingLeft: `${depth * 24 + 12}px` }}
            onClick={() => {
              if (hasChildren) {
                const newExpanded = new Set(expandedAccounts);
                if (isExpanded) {
                  newExpanded.delete(account.id);
                } else {
                  newExpanded.add(account.id);
                }
                setExpandedAccounts(newExpanded);
              }
            }}
          >
            <div className="flex items-center gap-2">
              {hasChildren ? (
                isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
              ) : <div className="w-4" />}
              <span className="font-mono text-sm text-muted-foreground">{account.account_code}</span>
              <span>{account.account_name}</span>
              <Badge className={`text-xs ${accountTypeColors[account.account_type]}`}>
                {account.account_type}
              </Badge>
            </div>
            {!account.is_header && (
              <span className={`font-mono ${account.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {account.current_balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>
          {hasChildren && isExpanded && children.map(child => renderAccount(child, depth + 1))}
        </div>
      );
    };
    
    return rootAccounts.map(account => renderAccount(account));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="accounting-title">
            Core Accounting
          </h1>
          <p className="text-muted-foreground mt-1">
            IPO-Ready Double-Entry Accounting System
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-[200px]">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Select Company" />
            </SelectTrigger>
            <SelectContent>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="chart-of-accounts">Chart of Accounts</TabsTrigger>
          <TabsTrigger value="journal-entries">Journal Entries</TabsTrigger>
          <TabsTrigger value="day-book">Day Book</TabsTrigger>
          <TabsTrigger value="general-ledger">General Ledger</TabsTrigger>
          <TabsTrigger value="trial-balance">Trial Balance</TabsTrigger>
          <TabsTrigger value="audit-log">Audit Trail</TabsTrigger>
        </TabsList>

        {/* Chart of Accounts Tab */}
        <TabsContent value="chart-of-accounts" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Chart of Accounts
            </h2>
            <div className="flex gap-2">
              {accounts.length === 0 && (
                <Button onClick={handleSeedAccounts} disabled={loading} data-testid="seed-accounts-btn">
                  <Settings className="h-4 w-4 mr-2" />
                  Initialize Default Accounts
                </Button>
              )}
              <Button variant="outline" onClick={() => setAccountDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Account
              </Button>
            </div>
          </div>

          <Card className="overflow-hidden">
            <div className="bg-slate-100 px-4 py-2 flex items-center justify-between text-sm font-semibold border-b">
              <span>Account</span>
              <span>Balance</span>
            </div>
            {accounts.length === 0 ? (
              <div className="p-12 text-center">
                <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No accounts configured</h3>
                <p className="text-muted-foreground mb-4">Initialize default chart of accounts to get started</p>
                <Button onClick={handleSeedAccounts} disabled={loading}>
                  <Settings className="h-4 w-4 mr-2" />
                  Initialize Default Accounts
                </Button>
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                {buildAccountTree()}
              </div>
            )}
          </Card>

          {/* Account Summary */}
          {accounts.length > 0 && (
            <div className="grid grid-cols-5 gap-4">
              {['asset', 'liability', 'equity', 'revenue', 'expense'].map(type => {
                const typeAccounts = accounts.filter(a => a.account_type === type && !a.is_header);
                const total = typeAccounts.reduce((sum, a) => sum + (a.current_balance || 0), 0);
                return (
                  <Card key={type} className="p-4">
                    <Badge className={`mb-2 ${accountTypeColors[type]}`}>{type}</Badge>
                    <p className="text-2xl font-bold">{total.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{typeAccounts.length} accounts</p>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Journal Entries Tab */}
        <TabsContent value="journal-entries" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Journal Entries
            </h2>
            <Button onClick={() => setJournalDialogOpen(true)} data-testid="new-journal-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Journal Entry
            </Button>
          </div>

          {/* Periods Section */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Accounting Periods
            </h3>
            <div className="flex gap-2 flex-wrap">
              {periods.map(period => (
                <Badge key={period.id} className={periodStatusColors[period.status]}>
                  {period.status === 'open' ? <Unlock className="h-3 w-3 mr-1" /> : <Lock className="h-3 w-3 mr-1" />}
                  {period.period_code} - {period.status}
                </Badge>
              ))}
              {periods.length === 0 && (
                <p className="text-muted-foreground text-sm">No periods. Initialize accounts first.</p>
              )}
            </div>
          </Card>

          {/* Journal Entries List */}
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Journal #</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Debit</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Credit</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {journalEntries.map(entry => (
                    <tr key={entry.id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-sm">{entry.journal_number}</td>
                      <td className="px-4 py-3 text-sm">{entry.entry_date}</td>
                      <td className="px-4 py-3 text-sm">{entry.description}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">{entry.entry_type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{entry.total_debit?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono">{entry.total_credit?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={journalStatusColors[entry.status]}>{entry.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-1 justify-center">
                          {entry.status === 'draft' && (
                            <Button size="sm" variant="outline" onClick={() => handleApproveEntry(entry.id)}>
                              Approve
                            </Button>
                          )}
                          {entry.status === 'approved' && (
                            <Button size="sm" className="bg-green-600" onClick={() => handlePostEntry(entry.id)}>
                              Post
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {journalEntries.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                        No journal entries yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Day Book Tab */}
        <TabsContent value="day-book" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Day Book
            </h2>
            <div className="flex gap-2">
              <Input 
                type="date" 
                value={dayBookDate} 
                onChange={(e) => setDayBookDate(e.target.value)}
                className="w-[180px]"
              />
              <Button onClick={loadDayBook}>
                <Search className="h-4 w-4 mr-2" />
                Load
              </Button>
            </div>
          </div>

          {dayBookData && (
            <Card>
              <div className="p-4 border-b bg-slate-50">
                <h3 className="font-semibold">Day Book for {dayBookData.date}</h3>
                <p className="text-sm text-muted-foreground">{dayBookData.entry_count} journal entries</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Journal #</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Account</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Debit</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayBookData.entries?.map((entry, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="px-4 py-2 font-mono text-sm">{entry.journal_number}</td>
                        <td className="px-4 py-2 text-sm">{entry.description}</td>
                        <td className="px-4 py-2 text-sm">
                          <span className="font-mono text-muted-foreground mr-2">{entry.account_code}</span>
                          {entry.account_name}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {entry.debit_amount > 0 ? entry.debit_amount.toLocaleString() : ''}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {entry.credit_amount > 0 ? entry.credit_amount.toLocaleString() : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 font-semibold">
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-right">Totals</td>
                      <td className="px-4 py-3 text-right font-mono">{dayBookData.total_debits?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono">{dayBookData.total_credits?.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* General Ledger Tab */}
        <TabsContent value="general-ledger" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              General Ledger
            </h2>
            <div className="flex gap-2">
              <Select value={selectedAccountForGL} onValueChange={setSelectedAccountForGL}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Select Account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.filter(a => !a.is_header).map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.account_code} - {a.account_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={loadGeneralLedger}>
                <Search className="h-4 w-4 mr-2" />
                Load
              </Button>
            </div>
          </div>

          {glData && (
            <Card>
              <div className="p-4 border-b bg-slate-50">
                <h3 className="font-semibold">{glData.account_code} - {glData.account_name}</h3>
                <div className="flex gap-6 mt-2 text-sm">
                  <span>Opening: <strong>{glData.opening_balance?.toLocaleString()}</strong></span>
                  <span className="text-green-600">Debits: <strong>{glData.total_debits?.toLocaleString()}</strong></span>
                  <span className="text-red-600">Credits: <strong>{glData.total_credits?.toLocaleString()}</strong></span>
                  <span>Closing: <strong>{glData.closing_balance?.toLocaleString()}</strong></span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Journal #</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Description</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Debit</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Credit</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50">
                      <td colSpan={5} className="px-4 py-2 text-sm font-semibold">Opening Balance</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold">{glData.opening_balance?.toLocaleString()}</td>
                    </tr>
                    {glData.entries?.map((entry, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="px-4 py-2 text-sm">{entry.date}</td>
                        <td className="px-4 py-2 font-mono text-sm">{entry.journal_number}</td>
                        <td className="px-4 py-2 text-sm">{entry.description}</td>
                        <td className="px-4 py-2 text-right font-mono">
                          {entry.debit_amount > 0 ? entry.debit_amount.toLocaleString() : ''}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {entry.credit_amount > 0 ? entry.credit_amount.toLocaleString() : ''}
                        </td>
                        <td className="px-4 py-2 text-right font-mono font-semibold">{entry.balance?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* Trial Balance Tab */}
        <TabsContent value="trial-balance" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Scale className="h-5 w-5" />
              Trial Balance
            </h2>
            <div className="flex gap-2">
              <Input 
                type="date" 
                value={trialBalanceDate} 
                onChange={(e) => setTrialBalanceDate(e.target.value)}
                className="w-[180px]"
              />
              <Button onClick={loadTrialBalance}>
                <Search className="h-4 w-4 mr-2" />
                Generate
              </Button>
            </div>
          </div>

          {trialBalanceData && (
            <Card>
              <div className="p-4 border-b bg-slate-50 flex justify-between items-center">
                <div>
                  <h3 className="font-semibold">Trial Balance as of {trialBalanceData.as_of_date}</h3>
                </div>
                <Badge className={trialBalanceData.is_balanced ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                  {trialBalanceData.is_balanced ? (
                    <><CheckCircle2 className="h-3 w-3 mr-1" /> Balanced</>
                  ) : (
                    <><AlertCircle className="h-3 w-3 mr-1" /> Not Balanced</>
                  )}
                </Badge>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Account Code</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Account Name</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Type</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Debit</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trialBalanceData.entries?.map((entry, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="px-4 py-2 font-mono text-sm">{entry.account_code}</td>
                        <td className="px-4 py-2 text-sm">{entry.account_name}</td>
                        <td className="px-4 py-2 text-center">
                          <Badge className={`text-xs ${accountTypeColors[entry.account_type]}`}>
                            {entry.account_type}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {entry.debit_balance > 0 ? entry.debit_balance.toLocaleString() : ''}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {entry.credit_balance > 0 ? entry.credit_balance.toLocaleString() : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 font-semibold">
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-right">Totals</td>
                      <td className="px-4 py-3 text-right font-mono">{trialBalanceData.total_debits?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono">{trialBalanceData.total_credits?.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit-log" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <History className="h-5 w-5" />
              Audit Trail
            </h2>
            <Button variant="outline" onClick={loadAuditLog}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Timestamp</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">User</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Entity</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Action</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id} className="border-b">
                      <td className="px-4 py-2 text-sm">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-2 text-sm">{log.user_email}</td>
                      <td className="px-4 py-2 text-sm">
                        <Badge variant="outline">{log.entity_type}</Badge>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <Badge className={
                          log.action === 'create' ? 'bg-green-100 text-green-800' :
                          log.action === 'post' ? 'bg-blue-100 text-blue-800' :
                          log.action === 'reverse' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }>{log.action}</Badge>
                      </td>
                      <td className="px-4 py-2 text-sm text-muted-foreground">{log.change_reason || '-'}</td>
                    </tr>
                  ))}
                  {auditLogs.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                        No audit logs yet. Click Refresh to load.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Journal Entry Dialog */}
      <Dialog open={journalDialogOpen} onOpenChange={setJournalDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              New Journal Entry
            </DialogTitle>
            <DialogDescription>
              Create a double-entry journal transaction. Debits must equal Credits.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Header */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Entry Date *</Label>
                <Input 
                  type="date" 
                  value={journalHeader.entry_date}
                  onChange={(e) => setJournalHeader({...journalHeader, entry_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Entry Type</Label>
                <Select 
                  value={journalHeader.entry_type} 
                  onValueChange={(v) => setJournalHeader({...journalHeader, entry_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="adjusting">Adjusting</SelectItem>
                    <SelectItem value="closing">Closing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Reference Number</Label>
                <Input 
                  value={journalHeader.reference_number}
                  onChange={(e) => setJournalHeader({...journalHeader, reference_number: e.target.value})}
                  placeholder="Invoice #, Check #, etc."
                />
              </div>
            </div>
            
            <div>
              <Label>Description *</Label>
              <Input 
                value={journalHeader.description}
                onChange={(e) => setJournalHeader({...journalHeader, description: e.target.value})}
                placeholder="Describe this transaction"
              />
            </div>

            {/* Lines */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-slate-100 px-4 py-2 flex items-center justify-between">
                <span className="font-semibold">Journal Lines</span>
                <Button size="sm" variant="outline" onClick={addJournalLine}>
                  <Plus className="h-4 w-4 mr-1" /> Add Line
                </Button>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-sm">Account</th>
                    <th className="px-3 py-2 text-left text-sm">Description</th>
                    <th className="px-3 py-2 text-right text-sm w-32">Debit</th>
                    <th className="px-3 py-2 text-right text-sm w-32">Credit</th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {journalLines.map((line, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">
                        <Select 
                          value={line.account_id} 
                          onValueChange={(v) => updateJournalLine(idx, 'account_id', v)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select account" />
                          </SelectTrigger>
                          <SelectContent>
                            {accounts.filter(a => !a.is_header).map(a => (
                              <SelectItem key={a.id} value={a.id}>
                                {a.account_code} - {a.account_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2">
                        <Input 
                          value={line.description || ''}
                          onChange={(e) => updateJournalLine(idx, 'description', e.target.value)}
                          placeholder="Line description"
                          className="h-9"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input 
                          type="number"
                          value={line.debit_amount || ''}
                          onChange={(e) => updateJournalLine(idx, 'debit_amount', parseFloat(e.target.value) || 0)}
                          className="h-9 text-right"
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input 
                          type="number"
                          value={line.credit_amount || ''}
                          onChange={(e) => updateJournalLine(idx, 'credit_amount', parseFloat(e.target.value) || 0)}
                          className="h-9 text-right"
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td className="px-3 py-2">
                        {journalLines.length > 2 && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500"
                            onClick={() => removeJournalLine(idx)}
                          >
                            ×
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100">
                  <tr>
                    <td colSpan={2} className="px-3 py-2 text-right font-semibold">Totals:</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{journalTotalDebit.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{journalTotalCredit.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Balance indicator */}
            <div className={`p-3 rounded-lg flex items-center gap-2 ${journalIsBalanced ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              {journalIsBalanced ? (
                <><CheckCircle2 className="h-5 w-5" /> Entry is balanced</>
              ) : (
                <><AlertCircle className="h-5 w-5" /> Entry not balanced - Difference: {Math.abs(journalTotalDebit - journalTotalCredit).toFixed(2)}</>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setJournalDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateJournalEntry}
                disabled={!journalIsBalanced || !journalHeader.description}
                className="bg-accent"
              >
                Create Journal Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
